"use client";

import { getPaidAdChannel, persistPaidAdChannel } from "@/lib/analytics/paid-ad";
import { useSearchParams } from "next/navigation";
import { type PostHog, usePostHog } from "posthog-js/react";
import { useEffect } from "react";

const UTM_SOURCE_KEY = "playmath.utmSource";
const FBCLID_KEY = "playmath.fbclid";
const UTM_CAMPAIGN_KEY = "playmath.utmCampaign";

function persistToSessionStorage(key: string, value: string | null): void {
  if (!value) return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Non-critical: sessionStorage may be unavailable
  }
}

function registerAttribution(
  posthog: PostHog | undefined,
  fbclid: string | null,
  utmCampaign: string | null
): void {
  if (!posthog?.register) return;
  if (fbclid) {
    posthog.register({ fbclid });
    posthog.register({ stored_fbclid: fbclid });
  }
  if (utmCampaign) {
    posthog.register({ utm_campaign: utmCampaign });
  }
}

/**
 * Persists utm_source in sessionStorage whenever it's present in the URL.
 * This allows email capture to attribute correctly even after the user navigates
 * to pages that don't include UTM params (e.g. header links).
 *
 * Also persists `fbclid` and registers it on the PostHog client so later events
 * (e.g. after client-side navigation drops query params) still carry Meta attribution.
 */
export function UtmSourceCapture() {
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const utmSource = searchParams.get("utm_source");
    const fbclidFromUrl = searchParams.get("fbclid");
    const utmCampaignFromUrl = searchParams.get("utm_campaign");

    persistToSessionStorage(UTM_SOURCE_KEY, utmSource);
    persistToSessionStorage(FBCLID_KEY, fbclidFromUrl);
    persistToSessionStorage(UTM_CAMPAIGN_KEY, utmCampaignFromUrl);

    // Detect + persist the paid-ad channel on landing (drives ad-tracker gating).
    persistPaidAdChannel(getPaidAdChannel(searchParams));

    const fbclid = fbclidFromUrl || getStoredFbclid();
    const utmCampaign = utmCampaignFromUrl || getStoredUtmCampaign();
    registerAttribution(posthog, fbclid, utmCampaign);
  }, [searchParams, posthog]);

  return null;
}

export function getStoredUtmSource(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(UTM_SOURCE_KEY);
  } catch {
    return null;
  }
}

export function getStoredUtmCampaign(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(UTM_CAMPAIGN_KEY);
  } catch {
    return null;
  }
}

export function getStoredFbclid(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(FBCLID_KEY);
  } catch {
    return null;
  }
}
